import { Box, Button, Popover, TextField, Tooltip } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { Dayjs } from "dayjs";
import React, { useMemo, useState } from "react";
import { LICENSE_WINDOW_RANGE_TOOLTIP } from "./availsPlusOptions";

function createRangeDay(rangeStart: Dayjs | null, rangeEnd: Dayjs | null) {
    return function RangeDay(props: PickersDayProps<Dayjs>) {
        const { day, outsideCurrentMonth, ...other } = props;

        const isStart = !!rangeStart && day.isSame(rangeStart, "day");
        const isEnd = !!rangeEnd && day.isSame(rangeEnd, "day");
        const isBetween =
            !!rangeStart &&
            !!rangeEnd &&
            day.isAfter(rangeStart, "day") &&
            day.isBefore(rangeEnd, "day");

        return (
            <PickersDay
                {...other}
                day={day}
                outsideCurrentMonth={outsideCurrentMonth}
                sx={(theme) => ({
                    ...(isBetween && {
                        bgcolor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                        borderRadius: 0,
                    }),
                    ...(isStart && {
                        borderTopLeftRadius: theme.shape.borderRadius,
                        borderBottomLeftRadius: theme.shape.borderRadius,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                    }),
                    ...(isEnd && {
                        borderTopRightRadius: theme.shape.borderRadius,
                        borderBottomRightRadius: theme.shape.borderRadius,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                    }),
                })}
            />
        );
    };
}

function getDateRangeLabel(from: Dayjs | null, to: Dayjs | null): string {
    if (from && to) {
        if (from.isSame(to, "day")) {
            return from.format("YYYY-MM-DD");
        }
        return `${from.format("YYYY-MM-DD")} - ${to.format("YYYY-MM-DD")}`;
    }
    if (from) {
        return from.format("YYYY-MM-DD");
    }
    if (to) {
        return to.format("YYYY-MM-DD");
    }
    return "";
}

interface LicenseWindowRangeFieldProps {
    label?: string;
    placeholder?: string;
    dateFrom: Dayjs | null;
    dateTo: Dayjs | null;
    error?: string | null;
    onChange: (from: Dayjs | null, to: Dayjs | null) => void;
    onApply?: () => void;
    fullWidth?: boolean;
    sx?: object;
}

export default function LicenseWindowRangeField({
    label = "License Window Range",
    placeholder = "Select a date or range",
    dateFrom,
    dateTo,
    error,
    onChange,
    onApply,
    fullWidth = false,
    sx,
}: LicenseWindowRangeFieldProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const RangeDay = useMemo(() => createRangeDay(dateFrom, dateTo), [dateFrom, dateTo]);

    const handleCalendarChange = (day: Dayjs | null) => {
        if (!day) return;
        if (!dateFrom || (dateFrom && dateTo)) {
            onChange(day.startOf("day"), null);
            return;
        }
        if (day.isBefore(dateFrom, "day")) {
            onChange(day.startOf("day"), null);
            return;
        }
        if (day.isSame(dateFrom, "day")) {
            onChange(dateFrom, day.startOf("day"));
            return;
        }
        onChange(dateFrom, day.startOf("day"));
    };

    return (
        <>
            <Tooltip title={LICENSE_WINDOW_RANGE_TOOLTIP} arrow placement="top">
                <TextField
                    size="small"
                    label={label}
                    placeholder={placeholder}
                    value={getDateRangeLabel(dateFrom, dateTo)}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    InputProps={{ readOnly: true }}
                    error={Boolean(error)}
                    fullWidth={fullWidth}
                    sx={sx}
                />
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box sx={{ p: 2 }}>
                    <DateCalendar
                        value={dateTo ?? dateFrom}
                        onChange={handleCalendarChange}
                        slots={{ day: RangeDay }}
                        showDaysOutsideCurrentMonth
                    />
                    {onApply && (
                        <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                setAnchorEl(null);
                                onApply();
                            }}
                        >
                            Apply
                        </Button>
                    )}
                </Box>
            </Popover>
        </>
    );
}
